import { BulleAide, FieldPatternInput, FieldRadioGroup } from '@workspace/core-ts/components';
import { prelevementStepContent } from '@workspace/core-ts/content';
import { StepContentConfig } from '@workspace/core-ts/utils';
import {
    DEBUT_CONTRAT_OPTIONS,
    isDateTimeNot30DaysInTheFuture,
    isDateTimeNotBefore,
    isDateTimeNotInThePast,
    isDateTimeNotOlderThanDays,
    isDateTimeNotOlderThanMonths,
} from '@workspace/utils';
import { addDays, addMinutes, format } from 'date-fns';
import { useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useAvenant, useContent, useLoiHamon, useSource } from '../../hooks';
import { MOTIF_AVENANT } from '../../utils/avenant';

const MOTIF_MISE_A_JOUR_DECLARATION = MOTIF_AVENANT.MISE_A_JOUR_DECLARATION;
const MOTIF_SPECIAL_6_MOIS: string[] = [MOTIF_AVENANT.MISE_A_JOUR_DECLARATION, MOTIF_AVENANT.CORRECTIF_AVENANT];

interface QuandDebuterContratProps {
    content?: StepContentConfig;
    hideLabel?: boolean;
}

/**
 * Composant de sélection de la date de début du contrat/avenant
 *
 * 3 modes d'affichage :
 * - selector : Affaire nouvelle → choix "Maintenant" / "Un autre jour"
 * - editable : Avenant conseiller → date modifiable
 * - readonly : Avenant client, motif 8, Loi Hamon → date en lecture seule
 */
export function QuandDebuterContrat({ content = prelevementStepContent, hideLabel = false }: QuandDebuterContratProps) {
    const methods = useFormContext<any>();
    const r = useContent();
    const isAvenant = useAvenant();
    const isLoiHamon = useLoiHamon();
    const { isConseiller } = useSource();

    // Valeurs du formulaire
    const quandDebuterContrat = methods.watch('DemandeContrat.QuandDebuterContrat');
    const dateHeureEffet = methods.watch('DemandeContrat.DateHeureEffet');
    const motifAvenant = methods.watch('DemandeTarif.Police.MotifAvenant');
    const dateEffetContrat = methods.watch('Devis.Police.DateEffetContrat');

    // Contexte métier
    const isAffaireNouvelle = !isAvenant && !isLoiHamon;
    const isMiseAJourDeclaration = isAvenant && motifAvenant === MOTIF_MISE_A_JOUR_DECLARATION;
    const isAvenantEditable = isAvenant && isConseiller && !isMiseAJourDeclaration && !isLoiHamon;

    // Mode d'affichage
    const mode = isAffaireNouvelle ? 'selector' : isAvenantEditable ? 'editable' : 'readonly';

    // Ref pour tracker la valeur précédente et éviter les boucles infinies
    const prevQuandDebuterRef = useRef<string | undefined>(undefined);

    // Mise à jour de la date lors du changement "Maintenant" / "Un autre jour"
    useEffect(() => {
        if (!isAffaireNouvelle) return;

        // Ne rien faire si la valeur n'a pas changé
        if (prevQuandDebuterRef.current === quandDebuterContrat) return;
        prevQuandDebuterRef.current = quandDebuterContrat;

        if (quandDebuterContrat === '0') {
            // "Maintenant" → date = now + 15min
            methods.setValue('DemandeContrat.DateHeureEffet', format(addMinutes(new Date(), 15), 'dd/MM/yyyy HH:mm'));
        } else if (quandDebuterContrat === '1' && !dateHeureEffet) {
            // "Un autre jour" → date = demain 00:00 (seulement si pas déjà rempli)
            methods.setValue('DemandeContrat.DateHeureEffet', format(addDays(new Date(), 1), 'dd/MM/yyyy HH:mm'));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quandDebuterContrat]);

    // Règles de validation selon le contexte
    const validationRules = useMemo(() => {
        const rules: Record<string, (value: string) => string | undefined> = {
            // Tous : pas plus de 30 jours dans le futur
            isDateTimeNot30DaysInTheFuture,
        };

        // Avenant : pas avant la date d'effet du contrat
        if (isAvenant && dateEffetContrat) {
            rules.isNotBeforeContractDate = isDateTimeNotBefore(dateEffetContrat);
        }

        // Conseiller : rétroactivité autorisée (60 jours ou 6 mois selon motif)
        // Client : pas de date dans le passé
        if (isConseiller) {
            const maxRetroMonths = MOTIF_SPECIAL_6_MOIS.includes(motifAvenant) ? 6 : 2;
            rules.isNotTooOld = maxRetroMonths === 6
                ? isDateTimeNotOlderThanMonths(6)
                : isDateTimeNotOlderThanDays(60);
        } else {
            rules.isDateTimeNotInThePast = isDateTimeNotInThePast;
        }

        return { validate: rules };
    }, [isConseiller, isAvenant, motifAvenant, dateEffetContrat]);

    // Conditions d'affichage
    const showDatePicker = mode === 'editable' || (mode === 'selector' && quandDebuterContrat === '1');
    const showReadOnlyDate = mode === 'readonly' || (mode === 'selector' && quandDebuterContrat !== '1');
    const readOnlyLabel = isAvenant ? "La date de début de l'avenant sera le" : 'La date de début du contrat sera le';

    return (
        <div className="space-y-3 w-full">
            {/* Sélecteur "Maintenant" / "Un autre jour" (affaire nouvelle uniquement) */}
            {mode === 'selector' && (
                <FieldRadioGroup
                    col={2}
                    defaultValue="0"
                    label={!hideLabel ? r(content.debutContratLabel) : undefined}
                    name="DemandeContrat.QuandDebuterContrat"
                    options={DEBUT_CONTRAT_OPTIONS}
                    type="small"
                />
            )}

            {/* Champ date modifiable */}
            {showDatePicker && (
                <FieldPatternInput
                    type="datetime"
                    label={r(content.dateHeureLabel) || "Date et heure d'effet de votre avenant"}
                    name="DemandeContrat.DateHeureEffet"
                    validation={validationRules}
                />
            )}

            {/* Date en lecture seule */}
            {showReadOnlyDate && (
                <BulleAide hideIcon>
                    {readOnlyLabel} <em>{dateHeureEffet}</em>
                </BulleAide>
            )}

            {/* Info signature électronique (avenant conseiller uniquement) */}
            {mode === 'editable' && (
                <BulleAide hideIcon>
                    Vous allez recevoir un lien de signature électronique pour votre avenant.
                </BulleAide>
            )}
        </div>
    );
}
